import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, Send } from "lucide-react";
import { flightSearchUrl } from "@/mock";
import { useTenant, BrandLogo } from "@/context/TenantContext";
import { apiSubscribe } from "@/api";
import { toast } from "sonner";

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    apiSubscribe(email).then(() => { setDone(true); toast.success("Subscribed! Deals incoming"); }).catch(() => toast.error("Try again"));
  };
  return (
    <div className="mt-5" data-testid="newsletter-signup">
      <h4 className="text-sm font-bold uppercase tracking-wider text-white">Get exclusive deals</h4>
      {done ? (
        <p className="mt-2 text-[13px] text-emerald-400">You're subscribed! Watch your inbox for offers.</p>
      ) : (
        <div className="mt-2 flex max-w-xs overflow-hidden rounded-full bg-white/10 ring-1 ring-white/15">
          <input data-testid="newsletter-email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Your email address"
            className="w-full bg-transparent px-4 py-2.5 text-[13px] text-white placeholder-slate-400 outline-none" />
          <button data-testid="newsletter-submit" onClick={submit} className="flex items-center justify-center bg-[#F5B32B] px-4 text-[#0A0F2E] hover:bg-[#e0a41f]">
            <Send className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const QUICK_LINKS = [
  { label: "Flights", to: "/" },
  { label: "Hotels", to: "/hotel-page" },
  { label: "Holiday", to: "/package" },
  { label: "Bus", to: "/bus" },
  { label: "Visa", to: "/visa" },
];

const ENQUIRY_LINKS = [
  { label: "Group Booking", to: "/group-enquiry" },
  { label: "Cab Enquiry", to: "/cab-enquiry" },
  { label: "Train Enquiry", to: "/train-enquiry" },
  { label: "Web Check-in", to: "/web-checkin" },
  { label: "Flight Tracking", to: "https://flightradar24.com/", external: true },
];

const POLICY_LINKS = [
  { label: "About Us", to: "/about" },
  { label: "Terms & conditions", to: "/terms" },
  { label: "Payment Security", to: "/payment-security" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Cancellation Policy", to: "/cancellation" },
  { label: "Refund Policy", to: "/refund" },
];

const FOOT_DOM = [
  { label: "Goa to Delhi", from: "GOI", to: "DEL" },
  { label: "Dubai to Delhi", from: "DXB", to: "DEL" },
  { label: "Lucknow to Mumbai", from: "LKO", to: "BOM" },
  { label: "Dehradun to Delhi", from: "DED", to: "DEL" },
  { label: "Kathmandu to Delhi", from: "KTM", to: "DEL" },
  { label: "Patna to Bangalore", from: "PAT", to: "BLR" },
];

const FOOT_INTL = [
  { label: "Bangkok to Mumbai", from: "BKK", to: "BOM" },
  { label: "Dubai to London", from: "DXB", to: "LHR" },
  { label: "Srinagar to Dubai", from: "SXR", to: "DXB" },
  { label: "Delhi to Toronto", from: "DEL", to: "YYZ" },
  { label: "Mumbai to Singapore", from: "BOM", to: "SIN" },
];

const FOOT_AIRLINES = [
  "Akasa Air",
  "AirAsia X",
  "Vietnam Airlines",
  "Airasia",
  "Sri Lankan Airlines",
  "Emirates",
  "IndiGo",
];

const PAYMENTS = [
  "UPI",
  "VISA",
  "Mastercard",
  "AMEX",
  "RuPay",
  "Paytm",
  "PhonePe",
  "BHIM",
  "G Pay",
  "MobiKwik",
];

const RouteRow = ({ title, items }) => (
  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-white/10 py-3 text-[13px]">
    <span className="font-bold text-white">{title} :</span>
    {items.map((r, i) => (
      <React.Fragment key={r.label}>
        <Link to={flightSearchUrl(r.from, r.to)} data-testid={`footer-route-${r.from}-${r.to}`} className="text-slate-300 transition-colors hover:text-[#F5B32B]">
          {r.label}
        </Link>
        {i < items.length - 1 && <span className="text-slate-500">|</span>}
      </React.Fragment>
    ))}
  </div>
);

const Footer = () => {
  const { contact: CONTACT } = useTenant();
  return (
  <footer className="bg-[#0A0F2E] text-slate-300">
    <div className="mx-auto max-w-7xl px-4 pt-12">
      {/* Link columns */}
      <div className="grid grid-cols-2 gap-8 pb-10 md:grid-cols-4 lg:grid-cols-5">
        <div className="col-span-2 lg:col-span-2">
          <div className="inline-block rounded-lg bg-white p-1.5"><BrandLogo className="h-12 w-auto" testId="footer-brand-logo" /></div>
          <p className="mt-4 max-w-sm text-[13px] leading-relaxed text-slate-400">
            {CONTACT.company} was established with a clear vision: to make the
            world accessible to everyone. Simple, smart, and stress-free travel
            planning.
          </p>
          <div className="mt-4 flex gap-2.5">
            {[Facebook, Instagram, Twitter, Youtube].map((Icon, i) => (
              <a
                key={i}
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-[#F5B32B] hover:text-[#0A0F2E]"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <NewsletterSignup />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-white">
            Quick Links
          </h4>
          <ul className="space-y-2 text-[13px]">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="transition-colors hover:text-[#F5B32B]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-white">
            Enquiry
          </h4>
          <ul className="space-y-2 text-[13px]">
            {ENQUIRY_LINKS.map((l) => (
              <li key={l.label}>
                {l.external ? (
                  <a href={l.to} target="_blank" rel="noreferrer" className="transition-colors hover:text-[#F5B32B]">
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} className="transition-colors hover:text-[#F5B32B]">
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-white">
            Policies
          </h4>
          <ul className="space-y-2 text-[13px]">
            {POLICY_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-policy-${l.to.slice(1)}`}
                  className="transition-colors hover:text-[#F5B32B]"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Route links */}
      <RouteRow title="Top Domestic Routes" items={FOOT_DOM} />
      <RouteRow title="Top International Routes" items={FOOT_INTL} />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-white/10 py-3 text-[13px]">
        <span className="font-bold text-white">Airlines :</span>
        {FOOT_AIRLINES.map((a, i) => (
          <React.Fragment key={a}>
            <span className="text-slate-300">{a}</span>
            {i < FOOT_AIRLINES.length - 1 && <span className="text-slate-500">|</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Copyright */}
      <div className="py-6 text-center">
        <p className="text-sm font-semibold text-white">
          © {new Date().getFullYear()} All Rights Reserved by {CONTACT.company}.
        </p>
        <p className="mx-auto mt-2 flex max-w-3xl items-start justify-center gap-1.5 text-xs leading-relaxed text-slate-400">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {CONTACT.address}
        </p>
        <div className="mx-auto mt-4 flex max-w-2xl flex-wrap items-center justify-center gap-x-8 gap-y-2 rounded-xl bg-white/5 px-6 py-3 text-sm">
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-[#F5B32B]">
            <Mail className="h-4 w-4 text-[#F5B32B]" /> Email : {CONTACT.email}
          </a>
          <span className="hidden text-slate-500 sm:inline">or, simply</span>
          <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 hover:text-[#F5B32B]">
            <Phone className="h-4 w-4 text-[#F5B32B]" /> Number : {CONTACT.phone}
          </a>
        </div>

        {/* Payment badges */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-white/10 pt-6">
          {PAYMENTS.map((p) => (
            <span
              key={p}
              className="rounded-md bg-white px-3 py-1.5 text-[11px] font-bold tracking-wide text-slate-700 shadow-sm"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);
};

export default Footer;
